<template>
    <div>
        <!-- Loading -->
        <transition name="puff-out-center">
            <ViewerLoading v-if="isLoading || !firstImageLoaded" />
        </transition>

        <!-- Error messages -->
        <div id="no-viewpoints-shared-link-error-container" v-if="errorLoadingViewpoints && !isLoading">
            <img :src="require('@/assets/img/invalid-link.png')" />

            <h1>Nenhuma foto encontrada</h1>
            <p class="text-16 text-weight-bold">Você não possui fotos para serem visualizadas neste projeto.</p>
        </div>

        <div id="no-viewpoints-shared-link-error-container" v-if="errorFetchingProjectData && !isLoading">
            <img :src="require('@/assets/img/invalid-link.png')" />

            <h1>Houve um problema ao carregar as informações do projeto</h1>
            <p class="text-16 text-weight-bold">
                Não foi possível carregar as informações do projeto. Verifique sua conexão. Caso o problema persista, entre em contato com o suporte.
            </p>
        </div>
        <GenericModal
            :title="$t('demo_viewer.welcome_message.title')"
            :description="$t('demo_viewer.welcome_message.description')"
            :confirmButtontext="$t('demo_viewer.welcome_message.continue')"
            ref="welcome-modal"
            video="https://www.youtube.com/watch?v=BJteRYZ_ZzM"
            @onContinue="$refs['welcome-modal'].hide()"
        />
        <div id="full-viewer-container" v-if="data && !errorLoadingViewpoints && !errorFetchingProjectData && !isLoading">
            <!-- =========================== START SIDEBAR COMPONENTS =========================== -->
            <LeftSidebar
                v-if="!isMobile"
                ref="left-sidebar"
                :isOpen="isFloorplansSidebarOpen || isIssuesSidebarOpen || isShareSidebarOpen || isToursSidebarOpen || isConstrucodeSidebarOpen"
                :options="sidebarOptions"
                @toggleFloorplansSidebar="onToggleFloorplansSidebar"
                @toggleIssuesSidebar="onToggleIssuesSidebar"
                @toggleShareSidebar="onToggleShareSidebar"
                @toggleToursSidebar="onToggleToursSidebar"
                @toggleConstrucodeSidebar="onToggleConstrucodeSidebar"
                @exitViewer="onExitViewer"
            />

            <LeftSubSidebarContainer
                v-if="!isMobile"
                ref="sub-sidebar"
                :isOpen="isFloorplansSidebarOpen || isIssuesSidebarOpen || isShareSidebarOpen || isToursSidebarOpen || isConstrucodeSidebarOpen"
                :isUsingSharedLink="false"
                :type="selectedSidebarType"
                :isMap="isMap"
                :project="project"
                :currentSector="currentSector"
                :currentFloorplan="currentFloorplan"
                :currentViewpoint="currentViewpoint"
                @sectorClick="(sector) => (currentSector = sector)"
                @floorplanClick="(floorplan) => (currentFloorplan = floorplan)"
                @viewpointClick="(data) => onViewpointChange(data)"
                @changeSelectedFloorplan="selectedFloorplanChanged"
                @changeSelectedSector="selectedSectorChanged"
                @close="onCloseSubsidebar"
                @openFloorplanFullscreen="isFloorplanFullscreenOpen = true"
                @onNewIssueRequestChangePosition="onRequestNewIssue"
                @onRequestCancelNewIssue="onRequestCancelNewIssue"
                @onNewIssueCreated="onNewIssueCreated"
                @onDocumentToggled="onConstrucodeDocumentToggled"
                @requireExpandConstrucodeDocument="requireExpandConstrucodeDocument"
            />

            <FloorplanViewer
                v-if="!isMobile && !isExitDialogOpen"
                :isEnabled="!(isFloorplansSidebarOpen || isIssuesSidebarOpen || isShareSidebarOpen || isToursSidebarOpen || isConstrucodeSidebarOpen)"
                :project="project"
                :isMap="isMap"
                :selectedFloorplan="selectedFloorplan"
                :currentViewpoint="currentViewpoint"
                @viewpointClick="(viewpoint) => onViewpointChange({ fp: selectedFloorplan, vp: viewpoint })"
                @floorplanChange="
                    (fp) => {
                        $refs['sub-sidebar'].selectedFloorplan = fp
                        selectedFloorplan = fp
                    }
                "
                @sectorChange="
                    ({ fp, sector }) => {
                        selectedSector = sector
                        selectedFloorplan = fp
                    }
                "
                @openFloorplanFullscreen="
                    () => {
                        if (isMap && selectedFloorplan.id == project.map.id) {
                            isFloorplansSidebarOpen = true
                            $refs['sub-sidebar'].isFloorplansSidebarOpen = false
                            $refs['sub-sidebar'].isSectorsSidebarOpen = false
                            $refs['sub-sidebar'].isShareSidebarOpen = false
                            $refs['sub-sidebar'].isToursSidebarOpen = false
                            $refs['sub-sidebar'].isConstrucodeSidebarOpen = false
                        } else {
                            isFloorplanFullscreenOpen = true
                        }
                    }
                "
            />

            <FloorplanFullscreen
                v-if="isFloorplanFullscreenOpen && !isMobile"
                :currentSector="selectedSector.id ? selectedSector : currentSector"
                :currentFloorplan="selectedFloorplan.id ? selectedFloorplan : currentFloorplan"
                :currentViewpoint="currentViewpoint"
                :project="project"
                :isMap="isMap"
                @floorplanChange="
                    (data) => {
                        $refs['sub-sidebar'].selectedFloorplan = data
                        selectedFloorplan = data
                    }
                "
                @viewpointClick="
                    (data) => {
                        onViewpointChange({ fp: selectedFloorplan, vp: data })
                    }
                "
                @closeFloorplanFullscreen="isFloorplanFullscreenOpen = false"
            />

            <!-- =========================== END SIDEBAR COMPONENTS =========================== -->
            <div
                id="viewer-content"
                :class="{
                    'is-mobile': isMobile,
                    'construcode-document-active': isConstrucodeDocumentActive,
                }"
            >
                <Split
                    ref="split"
                    :class="{
                        'mobile-split': isMobile,
                        'split-container': true,
                    }"
                    :direction="isMobile ? 'vertical' : 'horizontal'"
                    :gutterSize="imageComparisonEnabled ? 2 : 0"
                >
                    <SplitArea :size="imageComparisonEnabled ? 50 : 100">
                        <PanoramaViewer
                            v-if="currentPictures.left.id && !isModelLeft"
                            id="viewer-left"
                            ref="viewer-left"
                            side="left"
                            :imageComparisonEnabled="imageComparisonEnabled"
                            :lastViewpointId="lastViewpointId"
                            :coordinates="panoramaInstance.coordinates.left"
                            :picture="currentPictures.left"
                            :isMobile="isMobile"
                            @onCurrentCoordinatesChange="onCoordinatesChangedLeft"
                            @onStartDraggingPanorama="startDraggingPanorama"
                            @onStopDraggingPanorama="stopDraggingPanorama"
                            @onPanoramaInstanceChange="(pan) => (panoramaInstance.left = pan)"
                            @onImageLoad="leftViewerImageLoad"
                            @onHotspotClicked="(data) => onHotspotClicked('left', data)"
                            @onRequestDeleteHotspot="(data) => onRequestDeleteHotspot('left', data)"
                            @onRequestMoveHotspot="(data) => onRequestMoveHotspot('left', data)"
                            @onNewCoordsSelected="(data) => createNewHotspot('left', data)"
                            @onEditedCoordsSelected="(data) => editHotspotCoords('left', data)"
                        />
                    </SplitArea>
                    <SplitArea v-show="imageComparisonEnabled" :size="50">
                        <PanoramaViewer
                            v-if="currentPictures.right && currentPictures.right.id && !isModelRight"
                            id="viewer-right"
                            ref="viewer-right"
                            side="right"
                            :imageComparisonEnabled="imageComparisonEnabled"
                            :coordinates="panoramaInstance.coordinates.right"
                            :picture="currentPictures.right"
                            :isMobile="isMobile"
                            @onCurrentCoordinatesChange="onCoordinatesChangedRight"
                            @onStartDraggingPanorama="startDraggingPanorama"
                            @onStopDraggingPanorama="stopDraggingPanorama"
                            @onPanoramaInstanceChange="(pan) => (panoramaInstance.right = pan)"
                            @onImageLoad="rightViewerImageLoad"
                            @onHotspotClicked="(data) => onHotspotClicked('right', data)"
                            @onRequestDeleteHotspot="(data) => onRequestDeleteHotspot('right', data)"
                            @onRequestMoveHotspot="(data) => onRequestMoveHotspot('right', data)"
                            @onNewCoordsSelected="(data) => createNewHotspot('right', data)"
                        />
                    </SplitArea>
                </Split>

                <!-- =========================== START TOP UI COMPONENTS =========================== -->
                <!-- Left date selector -->
                <PictureDocumenter
                    v-if="canShowLeftPictureDocumenter"
                    :projectId="project.id"
                    :pictures="currentViewpoint.images"
                    :renders="currentViewpoint.renders"
                    :models="project.forge_models"
                    :imageComparisonEnabled="imageComparisonEnabled"
                    :selectedPicture="currentPictures.left"
                    :isConstrucodeDocumentActive="isConstrucodeDocumentActive"
                    @documenterOpened="(bool) => (isDocumenterOpen = bool)"
                    @documenterClosed="(bool) => (isDocumenterOpen = bool)"
                    @imageComparisonChange="onImageComparisonChange"
                    @pictureChange="onPictureLeftChange"
                />

                <!-- Right date selector -->
                <PictureDocumenter
                    v-if="canShowRightPictureDocumenter"
                    is-second-screen
                    :projectId="project.id"
                    :pictures="currentViewpoint.images"
                    :renders="currentViewpoint.renders"
                    :models="project.forge_models"
                    :imageComparisonEnabled="imageComparisonEnabled"
                    :selectedPicture="currentPictures.right"
                    @imageComparisonChange="onImageComparisonChange"
                    @pictureChange="onPictureRightChange"
                    @closedFromRightDocumenter="onPictureLeftChange"
                />

                <!-- Lock view button -->
                <LockPicture
                    :isMobile="isMobile"
                    :isLocked="isLocked"
                    :imageComparisonEnabled="imageComparisonEnabled && (!isModelLeft || !isModelRight)"
                    @onLockChanged="onLockChanged"
                />

                <!-- Up right location description -->
                <CurrentLocationDescription
                    v-if="canShowCurrentLocationDescription"
                    :reposition="(isModelLeft && !imageComparisonEnabled) || isModelRight"
                    :sector="currentSector"
                    :floorplan="currentFloorplan"
                    :viewpoint="currentViewpoint"
                />
                <!-- =========================== END TOP UI COMPONENTS =========================== -->

                <!-- =========================== START BOTTOM UI COMPONENTS =========================== -->
                <!-- Left navigation toolbar -->
                <BottomToolbar
                    ref="left-toolbar"
                    v-if="canShowLeftBottomToolbar"
                    :isMobile="isMobile"
                    :isFullscreenEnabled="isFullscreenEnabled"
                    :currentViewpoint="currentViewpoint"
                    :imageComparisonEnabled="imageComparisonEnabled"
                    :nextViewpoint="nextViewpoint"
                    :previousViewpoint="previousViewpoint"
                    :isConstrucodeDocumentActive="isConstrucodeDocumentActive"
                    @controlClickStart="(controlName) => onBottomToolbarControlClickStart(controlName, 'left')"
                    @controlClickStop="() => onBottomToolbarControlClickStop('left')"
                    @lookBackClick="() => onBottomToolbarLookBackClicked('left')"
                    @onPrevViewpointRequest="onPrevViewpointRequest"
                    @onNextViewpointRequest="onNextViewpointRequest"
                    @onRequestFullscreen="enableFullscreen"
                    @onRequestExitFullscreen="disableFullscreen"
                />

                <!-- Right navigation toolbar -->
                <BottomToolbar
                    ref="right-toolbar"
                    is-second
                    v-if="canShowRightBottomToolbar"
                    :isFullscreenEnabled="isFullscreenEnabled"
                    :isMobile="isMobile"
                    :currentViewpoint="currentViewpoint"
                    :imageComparisonEnabled="imageComparisonEnabled"
                    :nextViewpoint="nextViewpoint"
                    :previousViewpoint="previousViewpoint"
                    @controlClickStart="(controlName) => onBottomToolbarControlClickStart(controlName, 'right')"
                    @controlClickStop="() => onBottomToolbarControlClickStop('right')"
                    @lookBackClick="() => onBottomToolbarLookBackClicked('right')"
                    @onPrevViewpointRequest="onPrevViewpointRequest"
                    @onNextViewpointRequest="onNextViewpointRequest"
                    @onRequestFullscreen="enableFullscreen"
                    @onRequestExitFullscreen="disableFullscreen"
                />

                <!-- Right Floating Button -->
                <RightFloatingButton
                    v-if="canShowRightFloatingButton"
                    :options="floatingButtonOptions"
                    @screenshot="onRequestScreenshot"
                    @issue="() => onRequestNewIssue()"
                    @share="onRequestShare"
                />
                <!-- =========================== END BOTTOM UI COMPONENTS =========================== -->

                <!-- =========================== START OTHER UI COMPONENTS =========================== -->
                <ScreenshotPreview
                    v-if="screenshotOutput || loadingScreenshot"
                    :fileName="screenshotFileName"
                    :imageData="screenshotOutput"
                    @close="onRequestScreenshotPreviewClose"
                />

                <ActionConfirmationModal ref="action-confirmation-modal" :loading="isDeletingHotspot" />
                <!-- =========================== END OTHER UI COMPONENTS =========================== -->

                <!-- =========================== START MOBILE ONLY COMPONENTS =========================== -->

                <!-- <MobileBackButton v-if="isMobile && !isUsingSharedLink" @exitViewer="onExitViewer" /> -->

                <MobileFloorplansList
                    v-if="isMobile"
                    :currentSector="currentSector"
                    :currentFloorplan="currentFloorplan"
                    :currentViewpoint="currentViewpoint"
                    :project="project"
                    @onSelectViewpoint="onViewpointChange"
                />

                <MobileFloorplansNavigation
                    v-if="isMobile"
                    :currentSector="currentSector"
                    :currentFloorplan="currentFloorplan"
                    :currentViewpoint="currentViewpoint"
                    :project="project"
                    @onSelectViewpoint="onViewpointChange"
                />

                <MobileIssueView ref="mobile-issue-view" />

                <MobileImageComparison v-if="isMobile" :imageComparisonEnabled="imageComparisonEnabled" @imageComparisonChange="onImageComparisonChange" />

                <!-- =========================== END MOBILE ONLY COMPONENTS =========================== -->
            </div>
        </div>
    </div>
</template>

<script>
const LOADING_ICON = require('@/assets/img/misc/loading-white.svg')
// ==================
// Vuex
// ==================
import { mapState, mapGetters } from 'vuex'

// ====================
// Icons
// ====================
import 'vue-awesome/icons/camera'
import 'vue-awesome/icons/download'
import 'vue-awesome/icons/times'
import 'vue-awesome/icons/running'
import 'vue-awesome/icons/share-alt'

// ====================
// Watermark images
// ====================
import watermark from 'watermarkjs'
import IN_logo from '@/assets/img/IN.svg'

// ====================
// Sidebar
// ====================
import LeftSidebar from '@/components/360Viewer/Sidebar/LeftSidebar.vue'
import LeftSubSidebarContainer from '@/components/360Viewer/Sidebar/LeftSubSidebarContainer.vue'
import FloorplanViewer from '@/components/360Viewer/Sidebar/Floorplans/FloorplanViewer.vue'
import FloorplanFullscreen from '@/components/360Viewer/Sidebar/Floorplans/FloorplanFullscreen.vue'

// ====================
// UI
// ====================
import PictureDocumenter from '@/components/360Viewer/UI/PictureDocumenter.vue'
import ViewerLoading from '@/components/360Viewer/UI/ViewerLoading.vue'
import LockPicture from '@/components/360Viewer/UI/LockPicture.vue'
import CurrentLocationDescription from '@/components/360Viewer/UI/CurrentLocationDescription.vue'
import BottomToolbar from '@/components/360Viewer/UI/BottomToolbar.vue'
import RightFloatingButton from '@/components/Utils/NewRightFloatingButton.vue'
import ScreenshotPreview from '@/components/360Viewer/UI/ScreenshotPreview.vue'
import FullScreenImageViewer from '@/components/Utils/Images/FullScreenImageViewer'

// ====================
// Viewer Components
// ====================
import PanoramaViewer from '@/components/360Viewer/UI/PanoramaViewer'

// ====================
// Modals
// ====================
import ActionConfirmationModal from '@/components/Utils/Modals/ActionConfirmationModal'
import GenericModal from '@/components/Utils/Modals/GenericModal'

// ====================
// Mobile only
// ====================
// import MobileBackButton from '@/components/360Viewer/UI/Mobile/BackButton'
import MobileImageComparison from '@/components/360Viewer/UI/Mobile/ImageComparison'
import MobileFloorplansList from '@/components/360Viewer/UI/Mobile/FloorplansList'
import MobileFloorplansNavigation from '@/components/360Viewer/UI/Mobile/FloorplansNavigation'
import MobileIssueView from '@/components/360Viewer/UI/Mobile/IssueView'

// ====================
// External
// ====================
import mergeImages from 'merge-images'
import axios from 'axios'

// ====================
// Services
// ====================
import CINUtils from '@/services/Utils'

// ====================
// JSON data
// ====================
import getProjectView from '@/data/getProjectView'

export default {
    components: {
        //Sidebar
        LeftSidebar,
        LeftSubSidebarContainer,
        FloorplanFullscreen,

        // UI
        PictureDocumenter,
        ViewerLoading,
        LockPicture,
        CurrentLocationDescription,
        BottomToolbar,
        RightFloatingButton,
        ScreenshotPreview,
        FloorplanViewer,
        FullScreenImageViewer,

        // Viewer components
        PanoramaViewer,

        // Modals
        ActionConfirmationModal,
        GenericModal,

        // Mobile
        MobileImageComparison,
        MobileFloorplansList,
        MobileFloorplansNavigation,
        MobileIssueView,
    },
    computed: {
        ...mapState(['sidebar']),
        ...mapGetters('dateFilterViewer', ['getDatesByProjectId']),
        ...mapGetters('demo', ['getUser']),
        /**
         * Summary.
         * Get the active sidebar type: issues/floorplans
         *
         * @returns {String}
         */
        selectedSidebarType: function () {
            return this.isFloorplansSidebarOpen ? 'floorplans' : this.isIssuesSidebarOpen ? 'issues' : null
        },

        /**
         * Summary.
         * Screenshot name based on the current location
         *
         */
        screenshotFileName: function () {
            let date = this.currentPictures.left.date.replace('/', '_')
            let name = date + '-'
            if (this.currentFloorplan.map) {
                name += `${this.$t('keyword160')}-${this.currentViewpoint.name}`
            } else if (this.currentSector && this.currentSector.id) {
                name += `${this.$t('keyword160')}-${this.currentSector.name}-${this.currentFloorplan.name}-${this.currentViewpoint.name}`
            } else {
                name += `${this.currentFloorplan.name}-${this.currentViewpoint.name}`
            }

            return name.substr(0, 254)
        },

        /**
         * Summary.
         * Use this computed property to get the previous available viewpoint
         */
        previousViewpoint: function () {
            let currentIndex = 0
            if (this.currentFloorplan.viewpoints) {
                currentIndex = this.currentFloorplan.viewpoints.findIndex((vp, index) => vp.id == this.currentViewpoint.id)

                if (currentIndex - 1 < 0) {
                    return this.currentFloorplan.viewpoints[this.currentFloorplan.viewpoints.length - 1]
                } else {
                    return this.currentFloorplan.viewpoints[currentIndex - 1]
                }
            }
        },

        /**
         * Summary.
         * Use this computed property to get the next available viewpoint
         */
        nextViewpoint: function () {
            let currentIndex = 0
            if (this.currentFloorplan.viewpoints != undefined) {
                this.currentFloorplan.viewpoints.forEach((vp, index) => {
                    if (vp.id == this.currentViewpoint.id) {
                        currentIndex = index
                    }
                })
                if (currentIndex >= this.currentFloorplan.viewpoints.length - 1) {
                    return this.currentFloorplan.viewpoints[0]
                } else {
                    return this.currentFloorplan.viewpoints[currentIndex + 1]
                }
            }
        },

        /**
         * Summary.
         * Add any rules that prevent it to be shown.
         */
        canShowRightFloatingButton: function () {
            return (
                (this.showLocationPicker ||
                    this.selectedSidebarType == 'issues' ||
                    (this.$refs['sub-sidebar'] && this.$refs['sub-sidebar'].isFloorplansSidebarOpen) ||
                    !this.isMap) &&
                !this.isMobile &&
                (this.imageComparisonEnabled ? !this.isModelLeft || !this.isModelRight : !this.isModelLeft) &&
                (this.imageComparisonEnabled ? !this.isRenderLeft || !this.isRenderRight : !this.isRenderLeft)
            )
        },

        /**
         * Summary.
         * Add any rules that prevent it to be shown.
         */
        canShowLeftPictureDocumenter() {
            return this.showLocationPicker || (this.$refs['sub-sidebar'] && this.$refs['sub-sidebar'].isFloorplansSidebarOpen) || !this.isMap
        },

        /**
         * Summary.
         * Add any rules that prevent it to be shown.
         */
        canShowRightPictureDocumenter() {
            return (
                this.imageComparisonEnabled &&
                (this.showLocationPicker || (this.$refs['sub-sidebar'] && this.$refs['sub-sidebar'].isFloorplansSidebarOpen) || !this.isMap)
            )
        },

        /**
         * Summary.
         * Add any rules that prevent it to be shown.
         */
        canShowCurrentLocationDescription() {
            return (
                !this.isMobile && (this.showLocationPicker || (this.$refs['sub-sidebar'] && this.$refs['sub-sidebar'].isFloorplansSidebarOpen) || !this.isMap)
            )
        },

        /**
         * Summary.
         * Add any rules that prevent it to be shown.
         */
        canShowLeftBottomToolbar() {
            return (
                (this.showLocationPicker ||
                    this.selectedSidebarType == 'issues' ||
                    (this.$refs['sub-sidebar'] && this.$refs['sub-sidebar'].isFloorplansSidebarOpen) ||
                    !this.isMap) &&
                !this.isModelLeft
            )
        },

        /**
         * Summary.
         * Add any rules that prevent it to be shown.
         */
        canShowRightBottomToolbar() {
            return (
                this.imageComparisonEnabled &&
                !this.isMobile &&
                (this.showLocationPicker ||
                    this.selectedSidebarType == 'issues' ||
                    (this.$refs['sub-sidebar'] && this.$refs['sub-sidebar'].isFloorplansSidebarOpen) ||
                    !this.isMap) &&
                !this.isModelRight
            )
        },

        floatingButtonOptions() {
            let top = []
            let left = []

            // Top options
            top.push({
                tooltip: {
                    title: this.$t('FloatingOptions_7'),
                    side: 'left',
                },
                icon: {
                    name: 'comment-dots',
                    scale: '1',
                },
                emit: {
                    name: 'issue',
                    data: {},
                },
            })

            // Left options
            left.push({
                tooltip: {
                    title: this.$t('FloatingOptions_16'),
                    side: 'top',
                },
                icon: {
                    name: 'camera',
                    scale: '1',
                },

                emit: {
                    name: 'screenshot',
                    data: {},
                },
            })

            return {
                top,
                left,
            }
        },
    },
    watch: {
        /**
         * Summary.
         * Observe when the current pictures changes. We need to fetch issues and connections.
         *
         */
        currentPictures: {
            handler: async function () {
                try {
                    if (this.currentPictures.left && this.currentPictures.left.id) {
                        const currentLeftImage = this.currentPictures.left

                        this.currentPictures.left.issues = currentLeftImage.issues
                        this.currentPictures.left.connections = currentLeftImage.connections

                        if (this.$refs['viewer-left']) {
                            this.$refs['viewer-left'].reloadHotspots()
                        }
                    }

                    if (this.currentPictures.right && this.currentPictures.right.id) {
                        const currentRightImage = this.currentPictures.right

                        this.currentPictures.right.issues = currentRightImage.issues
                        this.currentPictures.right.connections = currentRightImage.connections

                        if (this.$refs['viewer-right']) {
                            this.$refs['viewer-right'].reloadHotspots()
                        }
                    }

                    if (this.isLoading) {
                        this.isLoading = false
                    }

                    if (this.$route.query.issue || this.$route.query.issue_id) {
                        this.onHotspotClicked('left', {
                            type: 'issue',
                            id: Number(this.$route.query.issue || this.$route.query.issue_id),
                        })

                        // reset query variables to avoid navigation BUGs
                        this.$router.push('/viewer/' + this.$route.params.id)
                    }
                } catch (err) {
                    console.error(err)
                    this.$notify({
                        group: 'foo',
                        type: 'error',
                        title: 'Não foi possível baixar os apontamentos desta imagem.',
                        text: 'Verifique sua conexão e tente novamente.',
                    })
                }
            },
            deep: true,
        },
    },
    data() {
        return {
            // =======================
            // New variables to modify
            // =======================
            data: getProjectView.data.getProject,

            // =======================
            // Flags and Loadings variables
            // =======================
            isLoading: true,
            errorLoadingViewpoints: false,
            isFirstLoad: true,
            firstImageLoaded: true,
            isMobile: false,
            isDocumenterOpen: false,
            isFullscreenEnabled: false,
            isExitDialogOpen: false,
            errorFetchingProjectData: false,

            // =======================
            // Project configs
            // =======================
            project: {},
            isMap: false,
            currentSector: {},
            currentFloorplan: {},
            currentViewpoint: {},
            lastViewpointId: null,

            // =======================
            // Forge viewer
            // =======================
            isModelLeft: false,
            isModelRight: false,
            isRenderLeft: false,
            isRenderRight: false,

            // =======================
            // Pictures configs
            // =======================
            currentPictures: {
                left: {},
                right: {},
            },
            isLocked: false,
            imageComparisonEnabled: false,

            // =======================
            // Panorama configs
            // =======================
            panoramaInstance: {
                left: null,
                right: null,
                coordinates: {
                    left: { pitch: 0, yaw: 0 },
                    right: { pitch: 0, yaw: 0 },
                },
                oldCoordinates: {
                    left: null,
                    right: null,
                },
            },

            isDraggingPanorama: false,
            draggingTimeout: null,

            // =======================
            // Date filter props
            // =======================
            datesToFilter: [],

            // =======================
            // Floorplans
            // =======================
            isFloorplanFullscreenOpen: false,
            isShowingMap: false,
            isShowingSectors: false,
            isShowingFloorplans: false,
            showLocationPicker: true,
            selectedFloorplan: {},
            selectedSector: {},

            // =======================
            // Sidebar configs
            // =======================
            isFloorplansSidebarOpen: false,
            isIssuesSidebarOpen: false,
            isToursSidebarOpen: false,
            isShareSidebarOpen: false,
            isConstrucodeSidebarOpen: false,
            sidebarOptions: [],

            // ==================
            // Screenshot
            // ==================
            screenshotOutput: null,
            loadingScreenshot: false,
            loadingScreenshotProgress: 0,

            // Hotspots (Issues/Connection)
            creatingNewHotspot: {
                active: false,
                coords: { pitch: 0, yaw: 0 },
                image_id: 0,
                type: null, // issue/connection
                thumbnail: null,
            },
            isDeletingHotspot: false,

            // Construcode
            isConstrucodeDocumentActive: false,
            currentConstrucodeDocument: null,
            currentConstrucodeArea: null,
        }
    },
    methods: {
        dataURLtoFile(dataurl, filename) {
            var arr = dataurl.split(','),
                mime = arr[0].match(/:(.*?);/)[1],
                bstr = atob(arr[1]),
                n = bstr.length,
                u8arr = new Uint8Array(n)

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n)
            }

            return new File([u8arr], filename, { type: mime })
        },
        // =====================
        // Configuration methods
        // =====================

        /**
         * Summary.
         * This method is used to configure page with vital informations.
         *
         * @param {Object} result - ApolloQuery result object
         */
        setup(result) {
            if (!result.data.errors) {
                this.project = Object.assign({}, result.data.getProject)

                this.captureDates = this.project.capture_dates

                this.isMap = this.project.map && this.project.map != null
                this.removeViewpointsWithoutPictures()

                this.sidebarOptions = [
                    {
                        type: 'divider',
                    },
                    {
                        type: 'item',
                        name: this.isMap ? 'map' : 'layer-group',
                        active: this.isMap,
                        action: 'toggleFloorplansSidebar',
                        tooltipText: this.$t('Components_Viewer_Sidebar_Tooltips_2'),
                    },
                    {
                        type: 'item',
                        name: 'comment-dots',
                        active: false,
                        action: 'toggleIssuesSidebar',
                        tooltipText: this.$t('Components_Viewer_Sidebar_Tooltips_3'),
                    },
                ]

                if (this.isFirstLoad) {
                    this.findFirstViewpoint()

                    if (!this.selectedFloorplan.url) this.selectedFloorplan = this.currentFloorplan
                    this.verifyPictureLeft()

                    this.isFirstLoad = false
                }
            } else {
                this.onFetchError()
            }
        },

        /**
         * Summary.
         * Used to fetch the autodesk forge viewer to be used on the viewer
         *
         */
        setupCDNScriptsAndHtmlConfigs() {
            let html = document.getElementsByTagName('html')
            html[0].classList.add('overflow-hidden')
        },

        /**
         * Summary.
         * Triggered when the ApolloQuery fetch emit an error
         *
         *
         */
        onFetchError() {
            this.errorFetchingProjectData = true
            this.isLoading = false
            this.firstImageLoaded = true
        },

        /**
         * Summary.
         * This method is used to remove viewpoints without images
         *
         */
        removeViewpointsWithoutPictures() {
            if (this.isMap) {
                // Clean viewpoints
                this.project.floorplans.forEach((fp) => {
                    if (fp.viewpoints)
                        fp.viewpoints = fp.viewpoints.filter((vp) => vp.images && (vp.images.length > 0 || (vp.renders != null && vp.renders.length > 0)))
                })
                if (this.project.map.viewpoints) {
                    this.project.map.viewpoints = this.project.map.viewpoints.filter(
                        (vp) => vp.images && (vp.images.length > 0 || (vp.renders != null && vp.renders.length > 0))
                    )
                }
                this.project.map.sectors.forEach((sct) => {
                    if (sct.floorplans) {
                        sct.floorplans.forEach((fp) => {
                            if (fp.viewpoints)
                                fp.viewpoints = fp.viewpoints.filter(
                                    (vp) => vp.images && (vp.images.length > 0 || (vp.renders != null && vp.renders.length > 0))
                                )
                        })
                    }
                })
                // Clean floorplans
                this.project.floorplans = this.project.floorplans.filter((fp) => fp.viewpoints && fp.viewpoints.length > 0)
                this.project.map.sectors.forEach((sct) => {
                    sct.floorplans = sct.floorplans.filter((fp) => fp.viewpoints && fp.viewpoints.length > 0)
                })
                // Clean sectors
                this.project.map.sectors = this.project.map.sectors.filter((sct) => sct.floorplans && sct.floorplans.length > 0)
            } else {
                // Clean viewpoints
                this.project.floorplans.forEach((fp) => {
                    if (fp.viewpoints)
                        fp.viewpoints = fp.viewpoints.filter((vp) => vp.images && (vp.images.length > 0 || (vp.renders != null && vp.renders.length > 0)))
                })
                // Clean floorplans
                this.project.floorplans = this.project.floorplans.filter((fp) => fp.viewpoints && fp.viewpoints.length > 0)
            }
        },
        /**
         * Summary.
         * Used to find the first viewpoint that contains pictures
         *
         * @returns {Boolean}
         */
        findFirstViewpoint() {
            this.currentFloorplan = this.project.floorplans[0]
            this.currentViewpoint = this.project.floorplans[0].viewpoints.find((v) => v.id == 5282)

            return true
        },
        /**
         * Summary.
         * use this method to find the viewpoint with the latest capture, if there is no viewpoint with images returns the first viewpoint found that has a model
         *
         * @return Viewpoint
         */
        findViewpointWithLastCapture(viewpoints) {
            if (viewpoints.some((v) => v.images.length)) {
                let vps = viewpoints
                    .filter((vp) => vp.images.length)
                    .map((vp) => ({
                        ...vp,
                        last: vp.images[0].date,
                    }))
                    .sort((a, b) => this.$moment(b.last, 'DD/MM/YYYY HH:mm:ss').unix() - this.$moment(a.last, 'DD/MM/YYYY HH:mm:ss').unix())
                return vps[0]
            } else if (viewpoints.some((v) => v.renders.length)) {
                return viewpoints.find((v) => v.renders.length)
            }
        },

        /**
         * Summary.
         * Use this method to find a viewpoint based on the query strings received by the $router object.
         *
         * @returns {Boolean}
         */
        findViewpointByQueryString() {
            let viewpointFound = false

            if (this.isMap) {
                if (this.$route.query['is-map-vp']) {
                    viewpointFound = this.searchMapViewpoint(this.$route.query.viewpoint)
                } else if (this.$route.query.sector) {
                    this.project.map.sectors.forEach((sector) => {
                        if (sector.id == this.$route.query.sector) {
                            this.currentSector = sector
                            this.currentSector.floorplans.forEach((fp) => {
                                if (fp.id == this.$route.query.floorplan) {
                                    this.currentFloorplan = fp
                                    this.currentFloorplan.viewpoints.forEach((vp) => {
                                        if (vp.id == this.$route.query.viewpoint) {
                                            this.currentViewpoint = vp
                                            viewpointFound = true
                                        }
                                    })
                                }
                            })
                        }
                    })
                } else {
                    let floorplans = this.project.map.sectors
                        .map((sec) => sec.floorplans)
                        .flat()
                        .concat(this.project.floorplans)
                    floorplans.forEach((fp) => {
                        if (fp.id == this.$route.query.floorplan) {
                            this.currentFloorplan = fp
                            this.currentFloorplan.viewpoints.forEach((vp) => {
                                if (vp.id == this.$route.query.viewpoint) {
                                    this.currentViewpoint = vp
                                    viewpointFound = true
                                }
                            })
                        }
                    })
                }
            } else {
                this.project.floorplans.forEach((fp) => {
                    if (fp.id == this.$route.query.floorplan) {
                        this.currentFloorplan = fp
                        this.currentFloorplan.viewpoints.forEach((vp) => {
                            if (vp.id == this.$route.query.viewpoint) {
                                this.currentViewpoint = vp
                                viewpointFound = true
                            }
                        })
                    }
                })
            }

            return viewpointFound
        },

        /**
         * Summary.
         * Used to search a map viewpoint based on ID.
         *
         * @param {Int} id
         *
         * @returns {Boolean}
         */
        searchMapViewpoint(id) {
            let viewpointFound = false

            this.project.map.viewpoints.forEach((vp) => {
                if (vp.id == id) {
                    viewpointFound = true
                    this.isMapViewpoint = true
                    this.currentSector = {}
                    this.currentFloorplan = {
                        id: this.project.map.id,
                        name: this.project.name,
                        url: this.project.map.url,
                        viewpoints: this.project.map.viewpoints,
                        sectors: this.project.map.sectors,
                    }

                    this.currentViewpoint = vp
                    this.onViewpointChange({ fp: this.currentFloorplan, vp })
                }
            })

            return viewpointFound
        },

        /**
         * Summary.
         * Callback to left viewer onImageLoad event. Used to set firstImageLoaded and try to alignImages if comparison mode is enabled.
         */
        leftViewerImageLoad() {
            this.firstImageLoaded = true

            if (this.imageComparisonEnabled && !this.isModelRight && !this.$refs['viewer-right'].isLoadingImage) {
                this.alignImages()
            }
        },

        /**
         * Summary.
         * Callback to right viewer onImageLoad event. Used to try to alignImages if comparison mode is enabled.
         */
        rightViewerImageLoad() {
            if (this.imageComparisonEnabled && !this.isModelLeft && !this.$refs['viewer-left'].isLoadingImage) {
                this.alignImages()
            }
        },

        /**
         * Summary.
         * Sets the left picture based on the current viewpoint, floorplan, sector and etc.
         *
         */
        verifyPictureLeft() {
            this.currentPictures.left = this.currentViewpoint.images.find((i) => i.id == 10693)
            this.currentPictures.right = this.currentViewpoint.images[0] ? this.currentViewpoint.images[0] : Object.assign({}, this.currentPictures.left)
        },

        /**
         * Summary.
         * Add a listner to verify if user press esc during fullscreen modo do disable tha control flag.
         */
        configureFullscreenESCListener() {
            const escHandler = (e) => {
                if (!document.fullscreenElement && !document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
                    this.isFullscreenEnabled = false
                } else {
                    this.isFullscreenEnabled = true
                }
            }

            document.addEventListener('fullscreenchange', escHandler)
            document.addEventListener('webkitfullscreenchange', escHandler)
            document.addEventListener('mozfullscreenchange', escHandler)
            document.addEventListener('MSFullscreenChange', escHandler)
        },

        /**
         * Summary.
         * Configure page to reflect the changes based on the URL.
         */
        configureIssueWhenRouteChanges() {
            if (Number(this.$route.query.viewpoint) != this.currentViewpoint.id) {
                let found = this.findViewpointByQueryString()

                if (found) {
                    // this will trigger the watch for currentPictures
                    this.verifyPictureLeft()
                }
            } else {
                this.verifyPictureLeft()

                // Simulate an issue click to focus on it
                this.onHotspotClicked('left', { type: 'issue', id: Number(this.$route.query.issue) })

                // reset query variables to avoid navigation BUGs
                this.$router.push('/viewer/' + this.$route.params.id)
            }
        },

        // =====================
        // Action methods
        // =====================

        /**
         * Summary.
         * Triggered when a hotspot is clicked inside the panorama
         *
         * @param {String} type - issue/connection
         * @param {Number} id
         */
        onHotspotClicked(side, { type, id }) {
            if (id != 'local-preview-hs') {
                if (type == 'issue') {
                    if (!this.isIssuesSidebarOpen && !this.isMobile) {
                        this.onToggleIssuesSidebar()
                    }

                    this.$nextTick((_) => {
                        if (this.currentPictures[side].issues) {
                            const selectedIssue = this.currentPictures[side].issues.find((issue) => issue.id == id)

                            if (selectedIssue) {
                                if (!this.isMobile) {
                                    const issuesPageInstance = this.$refs['sub-sidebar'].$refs['issues-sidebar'].$refs['issues'].$refs['viewer-issues-page']
                                    issuesPageInstance.selectedIssue = selectedIssue
                                }

                                // focus on selected issue
                                this.panoramaInstance[side].lookAt(selectedIssue.pitch, selectedIssue.yaw, this.panoramaInstance[side].getHfov(), 750, () => {
                                    if (this.isMobile) {
                                        this.$refs['mobile-issue-view'].open(selectedIssue)
                                    } else {
                                        if (side == 'right') {
                                            this.onCoordinatesChangedRight(750)
                                        } else {
                                            this.onCoordinatesChangedLeft(750)
                                        }
                                    }
                                })
                            }
                        }
                    })
                } else {
                    if (this.currentPictures[side].connections) {
                        const selectedConnection = this.currentPictures[side].connections.find((connection) => connection.id == id)

                        // focus on selected connection
                        this.panoramaInstance[side].lookAt(selectedConnection.pitch, selectedConnection.yaw, this.panoramaInstance[side].getHfov(), 750, () => {
                            if (side == 'right') {
                                this.onCoordinatesChangedRight(750)
                            } else {
                                this.onCoordinatesChangedLeft(750)
                            }

                            let currentSector = null
                            let currentFloorplan = null
                            let currentViewpoint = null

                            if (this.project.map) {
                                this.project.map.viewpoints.forEach((v) => {
                                    if (v.id == selectedConnection.viewpoint_target) {
                                        currentFloorplan = this.project.map
                                        currentViewpoint = v
                                    }
                                })

                                this.project.map.sectors.forEach((sec) => {
                                    sec.floorplans.forEach((f) => {
                                        f.viewpoints.forEach((v) => {
                                            if (v.id == selectedConnection.viewpoint_target) {
                                                currentSector = sec
                                                currentFloorplan = f
                                                currentViewpoint = v
                                            }
                                        })
                                    })
                                })
                            }

                            this.project.floorplans.forEach((f) => {
                                f.viewpoints.forEach((v) => {
                                    if (v.id == selectedConnection.viewpoint_target) {
                                        currentFloorplan = f
                                        currentViewpoint = v
                                    }
                                })
                            })

                            if (currentViewpoint) {
                                this.selectedSector = currentSector
                                this.currentSector = currentSector

                                this.currentFloorplan = currentFloorplan
                                this.selectedFloorplan = currentFloorplan

                                this.currentViewpoint = currentViewpoint
                                this.selectedViewpoint = currentViewpoint

                                this.verifyPictureLeft()
                            }
                        })
                    }
                }
            }
        },

        /**
         * Summary.
         * Triggered when user's request to move an active Hotspot
         *
         * @param {String} type - issue/connection
         * @param {Number} id
         */
        onRequestMoveHotspot(side, { type, id }) {
            if (id != 'local-preview-hs') {
                if (type == 'issue') {
                    let hotspot = document.getElementById('hotspot-' + id)
                    hotspot.style.opacity = 0.5
                    hotspot.style.pointerEvents = 'none'

                    if (this.$refs['viewer-left']) {
                        this.$refs['viewer-left'].startListeningToCoords(this.$t('Clique no novo local para mover o apontamento'), id, true)
                    }

                    if (this.$refs['viewer-right']) {
                        this.$refs['viewer-right'].startListeningToCoords(this.$t('Components_Viewer_NewIssue_1'), id, true)
                    }
                }
            }
        },

        /**
         * Summary.
         * Triggered when user's request to delete an active Hotspot
         *
         * @param {String} type - issue/connection
         * @param {Number} id
         */
        onRequestDeleteHotspot(side, { type, id }) {
            if (id != 'local-preview-hs') {
                if (type == 'issue') {
                    this.$refs['action-confirmation-modal'].show(
                        this.$t('Components_Viewer_DeleteHS_Issue_Title'),
                        this.$t('Components_Viewer_DeleteHS_Issue_Description'),
                        (confirmed) => {
                            if (confirmed) {
                                this.deleteIssue(side, id)
                            }
                        }
                    )
                }
            }
        },

        /**
         * Summary.
         * Request to delete an issue.
         *
         * @param {String} side - left/right
         * @param {Number} id
         */
        deleteIssue(side, id) {
            this.isDeletingHotspot = true
            this.$store.commit('demo/DELETE_ISSUE', id)
            setTimeout((_) => {
                this.isDeletingHotspot = false
            }, 2000)
            this.$refs['action-confirmation-modal'].hide()

            if (this.currentPictures.left.id == this.currentPictures.right.id) {
                // If is comparing the same images
                let deletedIssueIndex = this.currentPictures.left.issues.findIndex((i) => i.id == id)
                this.currentPictures.left.issues.splice(deletedIssueIndex, 1)
                this.currentPictures.right.issues.splice(deletedIssueIndex, 1)

                if (this.$refs['viewer-left']) {
                    this.$refs['viewer-left'].reloadHotspots()
                }

                if (this.$refs['viewer-right']) {
                    this.$refs['viewer-right'].reloadHotspots()
                }
            } else {
                let deletedIssueIndex = this.currentPictures[side].issues.findIndex((i) => i.id == id)
                this.currentPictures[side].issues.splice(deletedIssueIndex, 1)

                if (this.$refs['viewer-' + side]) {
                    this.$refs['viewer-' + side].reloadHotspots()
                }
            }
        },

        /**
         * Summary.
         * This method changes the pictures control vars when the current viewpoint changes.
         *
         * @param {Object} data - {fp (floorplan), vp (viewpoint)}
         */
        onViewpointChange(data) {
            if (data.vp.images.length && !this.isModelLeft) {
                this.isModelLeft = false
                this.isRenderLeft = false
            } else {
                this.isRenderLeft = true
            }

            if (this.currentFloorplan != data.fp) {
                this.lastViewpointId = null
            } else {
                this.lastViewpointId = this.currentViewpoint.id
            }

            this.currentFloorplan = data.fp
            this.selectedFloorplan = data.fp
            this.currentViewpoint = data.vp
            if (this.project.map) {
                this.currentSector = this.project.map.sectors.find((sec) => {
                    return sec.floorplans.find((fp) => {
                        return fp.viewpoints.find((vp) => vp.id == data.vp.id)
                    })
                })
            }
            let filterDates = []

            try {
                filterDates = this.getDatesByProjectId(this.project.id)
            } catch (Err) {
                console.warn('[CANT_GET_FILTER_DATES]', Err)
            }

            let filteredImages = this.currentViewpoint.images.filter((i) => filterDates.includes(i.date.substr(0, 10)))

            // LEFT IMAGE
            if (
                (this.currentPictures.left.url && this.currentViewpoint.images.length > 0) ||
                (!this.isModelLeft && this.currentViewpoint.renders.length == 0)
            ) {
                // IS IMAGE
                if (filteredImages.length) {
                    if (this.currentPictures.left.date) {
                        let sameDateImage = filteredImages.find((img) => img.date.substr(0, 10) == this.currentPictures.left.date.substr(0, 10))
                        if (sameDateImage) {
                            this.currentPictures.left = sameDateImage
                        } else {
                            this.currentPictures.left = filteredImages[0]
                        }
                    } else {
                        this.currentPictures.left = filteredImages[0]
                    }
                } else {
                    if (this.currentPictures.left.date) {
                        let sameDateImage = this.currentViewpoint.images.find((img) => img.date.substr(0, 10) == this.currentPictures.left.date.substr(0, 10))
                        if (sameDateImage) {
                            this.currentPictures.left = sameDateImage
                        } else {
                            this.currentPictures.left = this.currentViewpoint.images[0]
                        }
                    } else {
                        this.currentPictures.left = this.currentViewpoint.images[0]
                    }
                }
            } else {
                // IS RENDER OR MODEL
                if (!this.isModelLeft) {
                    this.currentPictures.left = this.currentViewpoint.renders[0]
                }
            }

            // RIGHT IMAGE
            if (
                (this.currentPictures.right.url && this.currentViewpoint.images.length > 0) ||
                (!this.isModelRight && this.currentViewpoint.renders.length == 0)
            ) {
                // IS IMAGE
                if (filteredImages.length) {
                    if (this.currentPictures.right.date) {
                        let sameDateImage = filteredImages.find((img) => img.date.substr(0, 10) == this.currentPictures.right.date.substr(0, 10))
                        if (sameDateImage) {
                            this.currentPictures.right = sameDateImage
                        } else if (filteredImages.length > 1) {
                            this.currentPictures.right = filteredImages[1]
                        } else {
                            this.currentPictures.right = filteredImages[0]
                        }
                    } else {
                        if (filteredImages.length > 1) {
                            this.currentPictures.right = filteredImages[1]
                        } else {
                            this.currentPictures.right = filteredImages[0]
                        }
                    }
                } else {
                    if (this.currentPictures.right.date) {
                        let sameDateImage = this.currentViewpoint.images.find((img) => img.date.substr(0, 10) == this.currentPictures.right.date.substr(0, 10))
                        if (sameDateImage) {
                            this.currentPictures.right = sameDateImage
                        } else if (this.currentViewpoint.images.length > 1) {
                            this.currentPictures.right = this.currentViewpoint.images[1]
                        } else {
                            this.currentPictures.right = this.currentViewpoint.images[0]
                        }
                    } else {
                        if (this.currentViewpoint.images.length > 1) {
                            this.currentPictures.right = this.currentViewpoint.images[1]
                        } else {
                            this.currentPictures.right = this.currentViewpoint.images[0]
                        }
                    }
                }
            } else {
                // IS RENDER OR MODEL
                if (!this.isModelRight) {
                    this.currentPictures.right = this.currentViewpoint.renders[0]
                }
            }

            this.isLocked = false

            if (this.isFloorplansSidebarOpen) {
                this.onCloseSubsidebar()
            }
        },

        /**
         * Summary.
         * This method apply some actions to forge viewers when user lock the views.
         *
         * @param {Boolean} isLocked
         */
        onLockChanged(isLocked) {
            this.isLocked = isLocked
            if (this.isLocked) {
                if (this.isModelLeft) {
                    this.$refs.leftModelViewer.setPosition()
                    this.panoramaInstance.coordinates.left = this.$refs.leftModelViewer.getCurrentCamera()
                } else {
                    this.panoramaInstance.coordinates.left.pitch = this.panoramaInstance.left.getPitch()
                    this.panoramaInstance.coordinates.left.yaw = this.panoramaInstance.left.getYaw()
                }

                if (this.isModelRight) {
                    this.$refs.rightModelViewer.setPosition()
                    this.panoramaInstance.coordinates.right = this.$refs.rightModelViewer.getCurrentCamera()
                } else {
                    this.panoramaInstance.coordinates.right.pitch = this.panoramaInstance.right.getPitch()
                    this.panoramaInstance.coordinates.right.yaw = this.panoramaInstance.right.getYaw()
                }
            } else {
                if (this.isModelLeft) {
                    this.$refs.leftModelViewer.resetPosition()
                }
                if (this.isModelRight) {
                    this.$refs.rightModelViewer.resetPosition()
                }
            }
        },

        /**
         * Summary.
         * Update right coordinates when moving the left view.
         */
        onCoordinatesChangedLeft(animationTime = false) {
            if (this.isLocked) {
                if (!this.isModelRight) {
                    this.panoramaInstance.right.lookAt(
                        this.panoramaInstance.coordinates.right.pitch + (this.panoramaInstance.left.getPitch() - this.panoramaInstance.coordinates.left.pitch),
                        this.panoramaInstance.coordinates.right.yaw + (this.panoramaInstance.left.getYaw() - this.panoramaInstance.coordinates.left.yaw),
                        this.panoramaInstance.right.getHfov(),
                        animationTime
                    )
                } else {
                    this.$refs.rightModelViewer.changeCamera(
                        this.panoramaInstance.coordinates.right.pitch -
                            (this.panoramaInstance.left.getPitch() - this.panoramaInstance.coordinates.left.pitch) +
                            90,
                        this.panoramaInstance.coordinates.right.yaw - (this.panoramaInstance.left.getYaw() - this.panoramaInstance.coordinates.left.yaw) - 90
                    )
                }
            }
        },

        /**
         * Summary.
         * Update left coordinates when moving the right view.
         */
        onCoordinatesChangedRight(animationTime = false) {
            if (this.isLocked) {
                if (!this.isModelLeft) {
                    this.panoramaInstance.left.lookAt(
                        this.panoramaInstance.coordinates.left.pitch + (this.panoramaInstance.right.getPitch() - this.panoramaInstance.coordinates.right.pitch),
                        this.panoramaInstance.coordinates.left.yaw + (this.panoramaInstance.right.getYaw() - this.panoramaInstance.coordinates.right.yaw),
                        this.panoramaInstance.left.getHfov(),
                        animationTime
                    )
                } else {
                    this.$refs.leftModelViewer.changeCamera(
                        this.panoramaInstance.coordinates.left.pitch -
                            (this.panoramaInstance.right.getPitch() - this.panoramaInstance.coordinates.right.pitch) +
                            90,
                        this.panoramaInstance.coordinates.left.yaw - (this.panoramaInstance.right.getYaw() - this.panoramaInstance.coordinates.right.yaw) - 90
                    )
                }
            }
        },

        /**
         * Summary.
         * Called when user toggle's the comparison view
         *
         * @param {Boolean} imageComparison - the new comparison status
         */
        onImageComparisonChange(imageComparison) {
            if (this.isConstrucodeDocumentActive) return

            this.isDocumenterOpen = imageComparison
            this.imageComparisonEnabled = imageComparison
            if (imageComparison) {
                let leftIndex = this.currentViewpoint.images.indexOf(this.currentPictures.left)

                if (leftIndex != -1) {
                    if (leftIndex == 0) {
                        this.currentPictures.right = this.currentViewpoint.images[leftIndex + 1] || this.currentViewpoint.renders[0]
                    } else {
                        this.currentPictures.right = this.currentViewpoint.images[leftIndex - 1] || this.currentViewpoint.renders[0]
                    }
                    this.isModelRight = false
                } else {
                    this.currentPictures.right = this.currentViewpoint.images[0] || this.currentViewpoint.renders[0]
                    this.isModelRight = false
                }

                if (!this.currentPictures.right) {
                    if (this.project.forge_models && this.project.forge_models.length) {
                        this.currentPictures.right = this.project.forge_models[0]
                        this.isModelRight = true
                    } else {
                        this.currentPictures.right = Object.assign({}, this.currentPictures.left)
                        this.isModelRight = this.isModelLeft
                    }
                }
            } else if (this.isModelRight) {
                this.isModelRight = false
            }

            if (this.$refs.leftModelViewer) {
                this.$refs.leftModelViewer.resize()
            }

            if (this.isModelLeft && this.isModelRight) {
                this.isLocked = false
            }

            this.$nextTick(() => {
                if (!this.isModelLeft && !this.isModelRight) {
                    if (!this.$refs['viewer-left'].isLoadingImage && !this.$refs['viewer-right'].isLoadingImage) {
                        this.alignImages()
                    }
                }
            })
        },

        /**
         * Summary.
         * It's used to set some important flags when the left panorama picture changes.
         *
         * @param {Object} config - { picture, isModel }
         */
        onPictureLeftChange({ picture, isModel, isRender }) {
            this.currentPictures.left = Object.assign({}, picture)
            this.isModelLeft = isModel
            this.isRenderLeft = isRender
            if (this.isModelLeft && this.isModelRight) {
                this.isLocked = false
            }
        },

        /**
         * Summary.
         * It's used to set some important flags when the left panorama picture changes.
         *
         * @param {Object} config - { picture, isModel }
         */
        onPictureRightChange({ picture, isModel, isRender }) {
            this.currentPictures.right = Object.assign({}, picture)
            this.isModelRight = isModel
            this.isRenderRight = isRender
            if (this.isModelLeft && this.isModelRight) {
                this.isLocked = false
            }
        },

        /**
         * Summary.
         * Used to calculate yaw offset from images between comparison selected and align both viewers on screen.
         */
        alignImages() {
            if (!this.imageComparisonEnabled) {
                return false
            }

            let leftIndex = this.currentViewpoint.images.indexOf(this.currentPictures.left)
            let rightIndex = this.currentViewpoint.images.indexOf(this.currentPictures.right)

            if (leftIndex != -1 && rightIndex != -1) {
                let offset = null

                if (leftIndex != rightIndex) {
                    let max,
                        min = 0
                    if (leftIndex > rightIndex) {
                        max = leftIndex
                        min = rightIndex
                    } else {
                        max = rightIndex
                        min = leftIndex
                    }
                    for (let i = min; i < max; i++) {
                        let currentImage = this.currentViewpoint.images[i]
                        if (currentImage.yaw_offset != null) {
                            if (offset == null) {
                                offset = 0
                            }
                            offset += currentImage.yaw_offset
                        } else {
                            /* SKIPPED ALIGNMENT DUE TO IMAGES NOT ALIGNED IN BETWEEN */
                            return false
                        }
                    }
                } else {
                    offset = 0
                }

                if (offset != null) {
                    this.onLockChanged(false)
                    this.$nextTick(() => {
                        let aligned,
                            to_align = null
                        if (leftIndex > rightIndex) {
                            to_align = this.panoramaInstance.right
                            aligned = this.panoramaInstance.left
                        } else {
                            to_align = this.panoramaInstance.left
                            aligned = this.panoramaInstance.right
                        }
                        to_align.lookAt(aligned.getPitch(), aligned.getYaw() + offset, aligned.getHfov(), 500, () => {
                            this.$nextTick(() => {
                                to_align.lookAt(aligned.getPitch(), aligned.getYaw() + offset, aligned.getHfov(), false)
                                this.$nextTick(() => {
                                    this.onLockChanged(true)
                                })
                            })
                        })
                    })
                }
            }
        },

        /**
         * Summary.
         * Go to the previous available viewpoint.
         */
        onPrevViewpointRequest() {
            this.onViewpointChange({
                fp: this.currentFloorplan,
                vp: this.previousViewpoint,
            })
        },

        /**
         * Summary.
         * Go to the next available viewpoint.
         */
        onNextViewpointRequest() {
            this.onViewpointChange({
                fp: this.currentFloorplan,
                vp: this.nextViewpoint,
            })
        },

        /**
         * Summary.
         * Enable the fullscreen mode on viewer's container.
         * OBS: We need to use the body tag to avoid tooltips problem when in fullscreen mode
         */
        enableFullscreen() {
            let panoramaContainer = document.querySelector('body')

            if (panoramaContainer.requestFullscreen) {
                panoramaContainer.requestFullscreen()
                this.isFullscreenEnabled = true
            } else if (panoramaContainer.mozRequestFullScreen) {
                /* Firefox */
                panoramaContainer.mozRequestFullScreen()
                this.isFullscreenEnabled = true
            } else if (panoramaContainer.webkitRequestFullscreen) {
                /* Chrome, Safari and Opera */
                panoramaContainer.webkitRequestFullscreen()
                this.isFullscreenEnabled = true
            } else if (panoramaContainer.msRequestFullscreen) {
                /* IE/Edge */
                panoramaContainer.msRequestFullscreen()
                this.isFullscreenEnabled = true
            }
        },

        /**
         * Summary.
         * Disable the fullscreen mode.
         */
        disableFullscreen() {
            if (document.exitFullscreen) {
                document.exitFullscreen()
                this.isFullscreenEnabled = false
            } else if (document.mozCancelFullScreen) {
                /* Firefox */
                document.mozCancelFullScreen()
                this.isFullscreenEnabled = false
            } else if (document.webkitExitFullscreen) {
                /* Chrome, Safari and Opera */
                document.webkitExitFullscreen()
                this.isFullscreenEnabled = false
            } else if (document.msExitFullscreen) {
                /* IE/Edge */
                document.msExitFullscreen()
                this.isFullscreenEnabled = false
            }
        },

        // ===================================
        // Bottom toolbar methods and envents
        // ===================================

        /**
         * Summary.
         * Called when user is pressing zoom or directinal actions.
         * If the view is locked the move is triggered on the other viewer too.
         *
         * @param {String} controlName
         * @param {String} direction - right/left
         */
        onBottomToolbarControlClickStart(controlName, direction) {
            if (this.isLocked) {
                if (this.$refs['viewer-left']) {
                    this.$refs['viewer-left'].startControl(controlName)
                }

                if (this.$refs['viewer-right']) {
                    this.$refs['viewer-right'].startControl(controlName)
                }
            } else {
                this.$refs['viewer-' + direction].startControl(controlName)
            }
        },

        /**
         * Summary.
         * Called when user stop's pressing zoom or directinal actions.
         * If the view is locked the move is stoped on the other viewer too.
         *
         * @param {String} direction - right/left
         */
        onBottomToolbarControlClickStop(direction) {
            if (this.isLocked) {
                if (this.$refs['viewer-left']) {
                    this.$refs['viewer-left'].stopControl()
                }

                if (this.$refs['viewer-right']) {
                    this.$refs['viewer-right'].stopControl()
                }
            } else {
                this.$refs['viewer-' + direction].stopControl()
            }
        },

        /**
         * Summary.
         * Called when user clicks on the look back button.
         * If the view is locked the move is triggered on the other viewer too
         *
         * @param {String} direction - right/left
         */
        onBottomToolbarLookBackClicked(direction) {
            if (this.isLocked) {
                if (this.$refs['viewer-left']) {
                    this.$refs['viewer-left'].lookBack()
                }

                if (this.$refs['viewer-right']) {
                    this.$refs['viewer-right'].lookBack()
                }
            } else {
                this.$refs['viewer-' + direction].lookBack()
            }
        },

        /**
         * Summary.
         * Called when user starts dragging viewer
         */
        startDraggingPanorama() {
            if (this.draggingTimeout) {
                clearTimeout(this.draggingTimeout)
            }

            this.isDraggingPanorama = true
        },

        /**
         * Summary.
         * Called when user stops dragging viewer
         */
        stopDraggingPanorama() {
            this.draggingTimeout = setTimeout(() => {
                this.isDraggingPanorama = false
            }, 250)
        },

        // ======================
        // Forge Methods
        // ======================
        onLeftModelCameraChanged({ pitch, yaw }) {
            if (this.isLocked && !this.isModelRight && !this.isDraggingPanorama) {
                this.panoramaInstance.right.lookAt(
                    this.panoramaInstance.coordinates.right.pitch + (pitch - this.panoramaInstance.coordinates.left.pitch),
                    this.panoramaInstance.coordinates.right.yaw - (yaw - this.panoramaInstance.coordinates.left.yaw),
                    this.panoramaInstance.right.getHfov(),
                    false
                )
            }
        },

        onRightModelCameraChanged({ pitch, yaw }) {
            if (this.isLocked && !this.isModelLeft && !this.isDraggingPanorama) {
                this.panoramaInstance.left.lookAt(
                    this.panoramaInstance.coordinates.left.pitch + (pitch - this.panoramaInstance.coordinates.right.pitch),
                    this.panoramaInstance.coordinates.left.yaw - (yaw - this.panoramaInstance.coordinates.right.yaw),
                    this.panoramaInstance.left.getHfov(),
                    false
                )
            }
        },

        // ======================
        // Share
        // ======================
        onRequestShare() {
            if (this.$refs['new-share-modal']) {
                this.$refs['new-share-modal'].show()
            }
        },

        onRequestNewLink() {
            if (!this.isShareSidebarOpen) {
                this.onToggleShareSidebar()
            }

            this.$nextTick((_) => {
                if (this.$refs['sub-sidebar'] && this.$refs['sub-sidebar'].$refs['share-sidebar']) {
                    this.$refs['sub-sidebar'].$refs['share-sidebar'].currentSelectedTab = 0
                    this.$refs['sub-sidebar'].$refs['share-sidebar'].createLink()
                } else {
                    console.warn('[TRYING_TO_OPEN_SIDEBAR_BEFORE_ITS_COMPLETLY_MOUNTED]', 'METHOD: onRequestNewLink')
                }
            })
        },

        onRequestNewViewer() {
            if (!this.isShareSidebarOpen) {
                this.onToggleShareSidebar()
            }

            this.$nextTick((_) => {
                if (this.$refs['sub-sidebar'] && this.$refs['sub-sidebar'].$refs['share-sidebar']) {
                    this.$refs['sub-sidebar'].$refs['share-sidebar'].currentSelectedTab = 1
                    this.$refs['sub-sidebar'].$refs['share-sidebar'].addViewers()
                } else {
                    console.warn('[TRYING_TO_OPEN_SIDEBAR_BEFORE_ITS_COMPLETLY_MOUNTED]', 'METHOD: onRequestNewViewer')
                }
            })
        },

        onRequestNewTour() {
            let route = this.$router.resolve({ path: `/new-tour/${this.$route.params.id}` })
            window.open(route.href, '_blank')
        },

        // ======================
        // Screenshot
        // ======================
        onRequestScreenshot() {
            this.loadingScreenshot = true
            this.loadingScreenshotProgress = 50
            if (this.imageComparisonEnabled) {
                this.generateScreenshotForSplittedView()
            } else {
                this.generateScreenshotForSingleView()
            }
        },

        /**
         * Summary.
         * Generate's a screenshot of both views
         *
         */
        async generateScreenshotForSplittedView() {
            let image_left = this.panoramaInstance.left
                .getRenderer()
                .render(
                    (this.panoramaInstance.left.getPitch() * Math.PI) / 180,
                    (this.panoramaInstance.left.getYaw() * Math.PI) / 180,
                    (this.panoramaInstance.left.getHfov() * Math.PI) / 180,
                    {
                        returnImage: true,
                    }
                )
            let image_right = this.panoramaInstance.right
                .getRenderer()
                .render(
                    (this.panoramaInstance.right.getPitch() * Math.PI) / 180,
                    (this.panoramaInstance.right.getYaw() * Math.PI) / 180,
                    (this.panoramaInstance.right.getHfov() * Math.PI) / 180,
                    {
                        returnImage: true,
                    }
                )
            let image = await this.getImageWidth(image_left)

            mergeImages(
                [
                    { src: image_left, x: 0, y: 0 },
                    { src: image_right, x: image.width + 6, y: 0 },
                ],
                { width: image.width * 2 + 6 }
            ).then((imgB64) => {
                this.loadingScreenshotProgress = 80
                watermark([imgB64, IN_logo])
                    .dataUrl((img, logo) => {
                        let context = img.getContext('2d')
                        context.save()

                        context.globalAlpha = 0.9
                        context.drawImage(logo, 10, 10)

                        context.restore()
                        return img
                    })
                    .render()
                    .dataUrl(
                        watermark.text.lowerRight(
                            `${this.currentSector ? this.currentSector.name : ''}`,
                            '24px sans-serif',
                            '#f5f5f5',
                            0.8,
                            this.currentSector ? 24 : 0
                        )
                    )
                    .render()
                    .dataUrl(
                        watermark.text.lowerRight(
                            `${this.currentFloorplan ? this.currentFloorplan.name : $t('map')}`,
                            '24px sans-serif',
                            '#f5f5f5',
                            0.8,
                            this.currentSector ? 48 : 24
                        )
                    )
                    .render()
                    .dataUrl(watermark.text.lowerRight(`${this.currentViewpoint.name}`, '24px sans-serif', '#f5f5f5', 0.8, this.currentSector ? 72 : 48))
                    .render()
                    .dataUrl(watermark.text.lowerLeft(`${this.currentPictures.left.date}`, '36px sans-serif', '#f5f5f5', 0.8))
                    .render()
                    .dataUrl(watermark.text.lowerRight(`${this.currentPictures.right.date}`, '36px sans-serif', '#f5f5f5', 0.8))
                    .then((url) => {
                        this.loadingScreenshotProgress = 100
                        this.loadingScreenshot = false
                        this.screenshotOutput = url
                    })
                    .catch((_) => {
                        this.loadingScreenshotProgress = 0
                        this.loadingScreenshot = false
                        this.screenshotOutput = null
                    })
            })
        },

        /**
         * Summary.
         * Generate's a screenshot of the left viewer
         *
         */
        generateScreenshotForSingleView() {
            let image = this.panoramaInstance.left
                .getRenderer()
                .render(
                    (this.panoramaInstance.left.getPitch() * Math.PI) / 180,
                    (this.panoramaInstance.left.getYaw() * Math.PI) / 180,
                    (this.panoramaInstance.left.getHfov() * Math.PI) / 180,
                    {
                        returnImage: true,
                    }
                )

            watermark([image, IN_logo])
                .dataUrl((img, logo) => {
                    let context = img.getContext('2d')
                    context.save()

                    context.globalAlpha = 0.9
                    context.drawImage(logo, 10, 10)

                    context.restore()
                    return img
                })
                .render()
                .dataUrl(
                    watermark.text.lowerRight(
                        `${this.currentFloorplan ? this.currentFloorplan.name : $t('map')}`,
                        '24px sans-serif',
                        '#f5f5f5',
                        0.8,
                        this.currentSector ? 48 : 24
                    )
                )
                .render()
                .dataUrl(watermark.text.lowerRight(`${this.currentViewpoint.name}`, '24px sans-serif', '#f5f5f5', 0.8, this.currentSector ? 72 : 48))
                .render()
                .dataUrl(watermark.text.lowerRight(`${this.currentPictures.left.date}`, '36px sans-serif', '#f5f5f5', 0.8))
                .then((url) => {
                    this.loadingScreenshotProgress = 100
                    this.loadingScreenshot = false
                    this.screenshotOutput = url
                })
                .catch((err) => {
                    this.loadingScreenshotProgress = 0
                    this.loadingScreenshot = false
                    this.screenshotOutput = null
                })
        },

        /**
         * Summary.
         * Get the original image dimensions
         *
         * @param {String} base64
         */
        getImageWidth(base64) {
            return new Promise((resolve) => {
                let img = new Image()
                img.onload = () => resolve({ width: img.width, height: img.height })
                img.src = base64
            })
        },

        /**
         * Summary.
         * Close screenshot preview modal
         *
         */
        onRequestScreenshotPreviewClose() {
            this.screenshotOutput = null
        },

        // ========================
        // Hotspots methods
        // ========================

        /**
         * Summary.
         * Triggered when an issue was successfully created.
         *
         * @param {Object} issue
         */
        onNewIssueCreated(issue) {
            let stillImageSelected =
                this.creatingNewHotspot.image_id == this.currentPictures.left.id || this.creatingNewHotspot.image_id == this.currentPictures.right.id
            if (this.currentPictures.left.id == this.currentPictures.right.id && stillImageSelected) {
                // If is comparing the same images
                this.currentPictures.right.issues.push(issue)
                this.currentPictures.left.issues.push(issue)

                if (this.$refs['viewer-left']) {
                    this.$refs['viewer-left'].reloadHotspots()
                }

                if (this.$refs['viewer-right']) {
                    this.$refs['viewer-right'].reloadHotspots()
                }
            } else if (stillImageSelected) {
                const side = this.creatingNewHotspot.image_id == this.currentPictures.left.id ? 'left' : 'right'

                this.currentPictures[side].issues.push(issue)
                if (this.$refs['viewer-' + side]) {
                    this.$refs['viewer-' + side].reloadHotspots()
                }
            }

            this.onRequestCancelNewIssue()
        },

        /**
         * Summary.
         * Triggered when user clicks on the new issue button
         *
         * @param {Object} data - used to add extra info to be reused in the new issue form.
         */
        onRequestNewIssue(data = null) {
            this.removeHotspotClickedLocalPreview()

            if (data) {
                this.creatingNewHotspot = {
                    ...this.creatingNewHotspot,
                    ...data,
                }
            } else {
                this.creatingNewHotspot.active = true
                this.creatingNewHotspot.type = 'issue'
            }

            if (this.isIssuesSidebarOpen) {
                this.onToggleIssuesSidebar()
            }

            if (this.$refs['viewer-left']) {
                this.$refs['viewer-left'].startListeningToCoords(this.$t('Components_Viewer_NewIssue_1'))
            }

            if (this.$refs['viewer-right']) {
                this.$refs['viewer-right'].startListeningToCoords(this.$t('Components_Viewer_NewIssue_1'))
            }
        },

        /**
         * Summary.
         * Use this to cancel an issue creation.
         */
        onRequestCancelNewIssue() {
            this.removeHotspotClickedLocalPreview()

            this.creatingNewHotspot = {
                active: false,
                coords: { pitch: 0, yaw: 0 },
                image_id: 0,
                type: null, // issue/connection
                thumbnail: null,
            }

            this.onToggleIssuesSidebar()
        },

        /**
         * Summary.
         * Add's a hotspot preview on user's click
         *
         * @param {String} side - left/right
         * @param {Object} coords - {pitch, yaw}
         */
        addHotspotClickedLocalPreview(side, coords) {
            if (this.imageComparisonEnabled && this.currentPictures.left.id == this.currentPictures.right.id) {
                // If is comparing the same images
                const preview = {
                    id: 'local-preview-hs',
                    pitch: coords.pitch,
                    yaw: coords.yaw,
                    status: 'closed',
                }

                this.currentPictures.right.issues.push(preview)
                this.currentPictures.left.issues.push(preview)

                if (this.$refs['viewer-left']) {
                    this.$refs['viewer-left'].reloadHotspots()
                }

                if (this.$refs['viewer-right']) {
                    this.$refs['viewer-right'].reloadHotspots()
                }
            } else {
                this.currentPictures[side].issues.push({
                    id: 'local-preview-hs',
                    pitch: coords.pitch,
                    yaw: coords.yaw,
                    status: 'closed',
                })

                if (this.$refs['viewer-' + side]) {
                    this.$refs['viewer-' + side].reloadHotspots()
                }
            }
        },

        /**
         * Summary.
         * Remove's any prevously created preview
         */
        removeHotspotClickedLocalPreview() {
            let leftPreviewIndex = -1
            let rightPreviewIndex = -1

            if (this.currentPictures.left.issues) {
                leftPreviewIndex = this.currentPictures.left.issues.findIndex((issue) => issue.id == 'local-preview-hs')
            }

            if (this.currentPictures.right.issues) {
                rightPreviewIndex = this.currentPictures.right.issues.findIndex((issue) => issue.id == 'local-preview-hs')
            }

            if (leftPreviewIndex != -1) {
                this.currentPictures.left.issues.splice(leftPreviewIndex, 1)

                if (this.$refs['viewer-left']) {
                    this.$refs['viewer-left'].reloadHotspots()
                }
            }

            if (rightPreviewIndex != -1) {
                this.currentPictures.right.issues.splice(rightPreviewIndex, 1)

                if (this.$refs['viewer-right']) {
                    this.$refs['viewer-right'].reloadHotspots()
                }
            }
        },

        /**
         * Summary.
         * Used to create
         */
        async createNewHotspot(side, { coords, image_id }) {
            if (side == 'left' && this.$refs['viewer-right']) {
                this.$refs['viewer-right'].stopListeningToCoords()
            } else if (this.$refs['viewer-left']) {
                this.$refs['viewer-left'].stopListeningToCoords()
            }

            this.creatingNewHotspot.coords = coords
            this.creatingNewHotspot.image_id = image_id

            this.addHotspotClickedLocalPreview(side, coords)

            if (!this.isIssuesSidebarOpen) {
                this.panoramaInstance[side].lookAt(coords.pitch, coords.yaw, this.panoramaInstance[side].getHfov(), 750, () => {
                    this.$nextTick((_) => {
                        // Avoid user moves during the transition.
                        this.panoramaInstance[side].lookAt(coords.pitch, coords.yaw, this.panoramaInstance[side].getHfov(), false)

                        this.$nextTick(async (_) => {
                            if (side == 'right') {
                                this.onCoordinatesChangedRight(750)
                            } else {
                                this.onCoordinatesChangedLeft(750)
                            }

                            try {
                                this.onToggleIssuesSidebar()
                                this.creatingNewHotspot.thumbnail = await this.$refs['viewer-' + side].generateThumbnailFromCurrentCoords()

                                this.$nextTick((_) => {
                                    this.$refs['sub-sidebar'].$refs['issues-sidebar'].openNewIssueForm({
                                        ...this.creatingNewHotspot,
                                        viewpoint: this.currentViewpoint,
                                        type: null,
                                    }) // Avoiding passing inadequate type for issue creation
                                })
                            } catch (Err) {
                                console.warn('[ISSUE_THUMB_FAILED]: FAILED GENERATING ISSUE THUMBNAIL', Err)
                            }
                        })
                    })
                })
            }
        },

        async editHotspotCoords(side, { coords, image_id, hotspot_id }) {
            if (side == 'left' && this.$refs['viewer-right']) {
                this.$refs['viewer-right'].stopListeningToCoords()
            } else if (this.$refs['viewer-left']) {
                this.$refs['viewer-left'].stopListeningToCoords()
            }

            let hotspot = document.getElementById('hotspot-' + hotspot_id)
            let hotspotIcon = hotspot.getElementsByClassName('hotspot-icon')[0]
            hotspot.style.pointerEvents = 'none'
            hotspotIcon.src = LOADING_ICON

            this.panoramaInstance[side].lookAt(coords.pitch, coords.yaw, this.panoramaInstance[side].getHfov(), 750, () => {
                this.$nextTick((_) => {
                    // Avoid user moves during the transition.
                    this.panoramaInstance[side].lookAt(coords.pitch, coords.yaw, this.panoramaInstance[side].getHfov(), false)

                    this.$nextTick(async (_) => {
                        if (side == 'right') {
                            this.onCoordinatesChangedRight(750)
                        } else {
                            this.onCoordinatesChangedLeft(750)
                        }

                        try {
                            let thumbnail = await this.$refs['viewer-' + side].generateThumbnailFromCurrentCoords()

                            try {
                                // let thumbUrl = await this.$Firebase.uploadBase64(thumbnail, 'image/png', 'ISSUE_THUMB')

                                let thumbUrl = URL.createObjectURL(this.dataURLtoFile(thumbnail))
                                let editedIssueIndex = this.currentPictures.left.issues.findIndex((i) => i.id == hotspot_id)
                                try {
                                    this.$set(this.currentPictures.left.issues[editedIssueIndex], 'pitch', coords.pitch)
                                    this.$set(this.currentPictures.left.issues[editedIssueIndex], 'yaw', coords.yaw)
                                    this.$set(this.currentPictures.right.issues[editedIssueIndex], 'pitch', coords.pitch)
                                    this.$set(this.currentPictures.right.issues[editedIssueIndex], 'yaw', coords.yaw)
                                } catch (err) {}
                                this.$refs['viewer-right'].reloadHotspots()
                                this.$refs['viewer-left'].reloadHotspots()

                                this.$store.commit('demo/MOVE_ISSUE', {
                                    pitch: coords.pitch,
                                    yaw: coords.yaw,
                                    id: hotspot_id,
                                    thumbnail: thumbUrl,
                                })
                            } catch (Err) {
                                console.warn('[THUMB_UPLOAD_FAILED] Thumb upload has failed', Err)
                            }
                        } catch (Err) {
                            console.warn('[ISSUE_THUMB_FAILED]: FAILED GENERATING ISSUE THUMBNAIL', Err)
                        }
                    })
                })
            })
        },

        // ==============================
        // Sidebar methods
        // ==============================

        /**
         * Summary.
         * Open floorplans sidebar and close all other active sidebars.
         *
         */
        onToggleFloorplansSidebar(status) {
            if (this.creatingNewHotspot.active) {
                this.onRequestCancelNewIssue()
            }

            this.showLocationPicker = !status && !this.isIssuesSidebarOpen
            this.isIssuesSidebarOpen = false
            this.isShareSidebarOpen = false
            this.isToursSidebarOpen = false
            this.isConstrucodeSidebarOpen = false
            this.isFloorplansSidebarOpen = !this.isFloorplansSidebarOpen
            this.sidebarOptions.forEach((e, i) => (this.sidebarOptions[i].active = false))
            this.sidebarOptions[2].active = this.isFloorplansSidebarOpen
        },

        /**
         * Summary.
         * Open issues sidebar and close all other active sidebars.
         *
         */
        onToggleIssuesSidebar(status) {
            this.isFloorplansSidebarOpen = false
            this.isShareSidebarOpen = false
            this.isToursSidebarOpen = false
            this.isConstrucodeSidebarOpen = false
            this.showLocationPicker = true
            this.isIssuesSidebarOpen = !this.isIssuesSidebarOpen
            this.sidebarOptions.forEach((e, i) => (this.sidebarOptions[i].active = false))
            this.sidebarOptions[3].active = this.isIssuesSidebarOpen
        },

        onToggleToursSidebar(status) {
            this.isFloorplansSidebarOpen = false
            this.isIssuesSidebarOpen = false
            this.isShareSidebarOpen = false
            this.isConstrucodeSidebarOpen = false
            this.isToursSidebarOpen = !this.isToursSidebarOpen
            this.sidebarOptions.forEach((e, i) => (this.sidebarOptions[i].active = false))
            this.sidebarOptions[4].active = this.isToursSidebarOpen
        },

        onToggleShareSidebar(status) {
            this.isFloorplansSidebarOpen = false
            this.isIssuesSidebarOpen = false
            this.isToursSidebarOpen = false
            this.isConstrucodeSidebarOpen = false
            this.isShareSidebarOpen = !this.isShareSidebarOpen
            this.sidebarOptions.forEach((e, i) => (this.sidebarOptions[i].active = false))
            this.sidebarOptions[5].active = this.isShareSidebarOpen
        },

        onToggleConstrucodeSidebar() {
            this.isFloorplansSidebarOpen = false
            this.isIssuesSidebarOpen = false
            this.isToursSidebarOpen = false
            this.isShareSidebarOpen = false
            this.isConstrucodeSidebarOpen = !this.isConstrucodeSidebarOpen
            this.sidebarOptions.forEach((e, i) => (this.sidebarOptions[i].active = false))
            this.sidebarOptions[7].active = this.isConstrucodeSidebarOpen
        },

        /**
         * Summary.
         * close sidebar.
         *
         */
        onCloseSubsidebar() {
            if (this.creatingNewHotspot.active) {
                this.$refs['sub-sidebar'].$refs['issues-sidebar'].creatingNewIssue = false
                this.onRequestCancelNewIssue()
            } else {
                this.isFloorplansSidebarOpen = false
                this.isIssuesSidebarOpen = false
                this.isShareSidebarOpen = false
                this.isToursSidebarOpen = false
                this.isConstrucodeSidebarOpen = false
                this.showLocationPicker = true
                this.sidebarOptions.forEach((e, i) => (this.sidebarOptions[i].active = false))
            }
        },

        /**
         * Summary.
         * Open exit viewer dialog
         *
         */
        onExitViewer() {
            if (!this.isMobile) {
                this.isFloorplansSidebarOpen = false
                this.isIssuesSidebarOpen = false
                this.isShareSidebarOpen = false
                this.isToursSidebarOpen = false
                this.isConstrucodeSidebarOpen = false
                this.isExitDialogOpen = true

                if (this.isFullscreenEnabled) {
                    this.disableFullscreen()
                }

                this.$refs['left-sidebar'].showExitDialog(() => {
                    this.isExitDialogOpen = false
                })
            }
        },

        // ========================
        // Floorplans
        // ========================
        /**
         * Summary.
         * Change SelectedFloorplan to show in FloorplanFullscreen
         *
         */
        selectedFloorplanChanged(data) {
            this.selectedFloorplan = data
        },

        selectedSectorChanged(data) {
            this.selectedSector = data
        },

        // ========================
        // Successful tour creation
        // ========================
        checkRouteTourCreation() {
            if (this.$route.query['tour-created'] != null) {
                let interval = setInterval(() => {
                    if (this.$refs['success-creation-modal']) {
                        this.$refs['success-creation-modal'].show(this.project.id, this.$route.query['tour-created'])
                        clearInterval(interval)
                    }
                }, 500)
            }
        },

        // ========================
        // CONSTRUCODE
        // ========================
        onConstrucodeDocumentToggled(status) {
            if (status && this.imageComparisonEnabled) {
                this.onImageComparisonChange()
            }

            this.isConstrucodeDocumentActive = status

            if (this.$refs['viewer-left']) {
                this.$refs['viewer-left'].setupPanorama()

                if (this.creatingNewHotspot.active) {
                    this.panoramaInstance['left'].lookAt(
                        this.creatingNewHotspot.coords.pitch,
                        this.creatingNewHotspot.coords.yaw,
                        this.panoramaInstance['left'].getHfov(),
                        750
                    )
                }
            }
        },

        requireExpandConstrucodeDocument(file, area) {
            this.currentConstrucodeDocument = file
            this.currentConstrucodeArea = area

            if (this.$refs['ccode-file-viewer'] && file) {
                this.$refs['ccode-file-viewer'].show(file.pathURL)
            }
        },
    },

    async mounted() {
        this.$store.commit('demo/SET_USER', {
            name: 'Você',
            surname: '',
            role: '',
            email: 'voce@mail.com',
            phone: '',
        })

        this.$i18n.locale = this.$route.query.lang !== undefined ? this.$route.query.lang : 'pt_BR'
        this.isMobile = this.$Mobile.verify()
        this.configureFullscreenESCListener()

        this.setupCDNScriptsAndHtmlConfigs()

        // Add setup manually
        this.setup(getProjectView)

        setTimeout((_) => {
            // MODAL DE ENTRADA
            this.$refs['welcome-modal'].show()
        }, 1000)
    },

    beforeDestroy() {
        let html = document.getElementsByTagName('html')
        html[0].classList.remove('overflow-hidden')
    },

    beforeRouteUpdate(to, from, next) {
        if (to.fullPath != from.fullPath) {
            next()
            if (to.query.issue) {
                this.configureIssueWhenRouteChanges()
            }
        }
    },
}
</script>
<style lang="scss">
// Global style to change the split-area divider color
.gutter-horizontal,
.gutter-vertical {
    background-color: var(--primary-1);
    pointer-events: none;
}

#fullscreen-image-viewer {
    .actions {
        .construcode-logo {
            img {
                padding: 6px;
                height: 100%;
                width: auto;
            }
        }
    }
}
</style>
<style lang="scss" scoped>
@import '@/scss/views/Project/360-viewer.scss';
</style>
